/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2016-2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
*/

// Can we make a directive out of this which depends on the contenteditor directive, such that this directive,
// during the prelink, could create an instance of the extension, and give it to the contentEditor controller for
// use during the initialization of the editor, but since it is in a directive, we have access to such things as
// scope and other controllers, so we can do things like make frame changes and such?

/**
* AngularJS Factory service to provide extended functionality for blocks on a page.
*/
angular.module('bulbApp.contentEditorModule')
  .factory(
    'BulbInsertBlockExtensionFactory',
    [
      '$',
      '$compile',
      '$cookies',
      '$log',
      '$modal',
      '$q',
      '$timeout',
      '$translate',
      '$window',
      'MediumEditor',
      'Restangular',
      'AeroDialogService',
      'AssetUploadEventService',
      'AssetUploadHttpService',
      'BulbAuthService',
      'BulbAssetLibraryGoogleDriveService',
      'BulbAssetLibraryGooglePhotoService',
      'BulbAssetLibraryOneDriveService',
      'BulbAssetLibrarySnapshotPdfService',
      'BulbAssetLibraryService',
      'BulbAssetLibraryUnsplashService',
      'BulbAssetLibraryUploadService',
      'BulbBrowserService',
      'BulbBusyCoverService',
      'BulbBusyService',
      'BulbCKEStateService',
      'BulbCommentService',
      'BulbErrorService',
      'BulbImageCarouselService',
      'BulbModalService',
      'RecordEventService',
      'SerialSaveService',
      'UnitStateStore',
      'UppyDashboardService',
      'CONFIG',
      'CONTENT_EDITOR',
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      BulbInsertBlockExtensionFactory,
    ],
  );

function BulbInsertBlockExtensionFactory(
  $,
  $compile,
  $cookies,
  $log,
  $modal,
  $q,
  $timeout,
  $translate,
  $window,
  MediumEditor,
  Restangular,
  AeroDialogService,
  AssetUploadEventService,
  AssetUploadHttpService,
  BulbAuthService,
  BulbAssetLibraryGoogleDriveService,
  BulbAssetLibraryGooglePhotoService,
  BulbAssetLibraryOneDriveService,
  BulbAssetLibrarySnapshotPdfService,
  BulbAssetLibraryService,
  BulbAssetLibraryUnsplashService,
  BulbAssetLibraryUploadService,
  BulbBrowserService,
  BulbBusyCoverService,
  BulbBusyService,
  BulbCKEStateService,
  BulbCommentService,
  BulbErrorService,
  BulbImageCarouselService,
  BulbModalService,
  RecordEventService,
  SerialSaveService,
  UnitStateStore,
  UppyDashboardService,
  CONFIG,
  CONTENT_EDITOR,
) {
  // Key Codes that can change or modify the selection:
  const selectionKeyCodes = [
    8, // backspace
    13, // enter
    37, // left arrow
    38, // up arrow
    39, // right arrow
    40, // down arrow
    46, // delete
  ]; //

  const createNewParagraphData = function createNewParagraphData() {
    return BulbCommentService.getParagraphIds(1).then((paragraphIds) => `<p id="${paragraphIds[0]}"><br/></p>`);
  };

  const uppyAllowedImageFileTypes = ['image/*', '.heic', '.heif'];
  const uppyAllowedVideoFileTypes = ['video/*'];
  const uppyAllowedAudioFileTypes = ['audio/*'];

  const BulbInsertBlockExtension = MediumEditor.Extension.extend({
    name: 'bulbInsertBlock',

    selection: null,

    // TODO UNDO : how do we undo/redo an upload?

    mediaFileUploadOptions: {
      url: '/b/rest/assets',
      paramName: 'files[]',
      // TODO CLEAN : get these file types from config
      acceptFileTypes: /(\.|\/)(gif|jpe?g|png|3g2|3gp|asf|avi|flv|m4v|mkv|mov|mp4|mpg|vob|wmv|quicktime)$/i,
      maxChunkSize: 5242880, // TODO CLEAN : need to get this from CONFIG after merge with dev
      maxFileSize: 10000000000, // TODO CLEAN : need to get this from CONFIG after merge with dev
      headers: {
        'X-XSRF-TOKEN': $cookies.get('XSRF-TOKEN'),
      },
    },

    attachmentUploadOptions: {
      url: '/b/rest/assets',
      paramName: 'files[]',
      maxChunkSize: 5242880,
      maxFileSize: 10000000000, // TODO CLEAN : need to get this from CONFIG after merge with dev
      headers: {
        'X-XSRF-TOKEN': $cookies.get('XSRF-TOKEN'),
      },
    },

    defaults: {
      messages: { // TODO CLEAN : these need to be translated, or removed based on better error handling.
        acceptFileTypesError: 'This file is not in a supported format: ',
        maxFileSizeError: 'This file is too big: ',
      },
    },

    /**
     * Initialization function for factory extension.
     */
    init: function init() {
      // Grab the current page from the above scope. Look at contenteditor-directive.js file.
      this.page = this.scope.contentEditorCtrl.page;
      // eslint-disable-next-line no-param-reassign
      $window.Restangular = Restangular;

      // Adding this factory onto the window object, so we could potentially reference it in our Angular Framework.
      // We could upgrade this factory to a pure Angular Service, which would be better, however the time to refactor
      // This service would outweigh any cost ( at the moment -- we are going to have to do this at some point ).
      // eslint-disable-next-line no-param-reassign
      $window.bulbVars.BulbInsertBlockExtensionFactory = this;
    },

    handleClick: function handleClick(event) {
      // Check to see if an inserted media element has been selected.
      const $element = $(event.target);
      const $selectableBlockElement = $element.closest('[selectable-block]');
      if ($selectableBlockElement.length) {
        // We are inside a block.  However, check to see if we are also inside a contenteditable, because
        // that will have precedence over the block.
        const $editableElement = $element.closest('[contenteditable="true"]');
        if ($editableElement.length && $.contains($selectableBlockElement[0], $editableElement[0])) {
          this.unselectBlock();
        } else {
          this.selectBlock($selectableBlockElement[0]);
        }
      } else {
        this.unselectBlock();
      }
    },

    unselectBlock: function unselectBlock() {
      $(this.editor).find('.selected-block').removeClass('selected-block');
      this.blockSelected(null);
      this.selection = null;
      this.updateButtons();
    },

    handleKeydown: function handleKeydown(event) {
      // CTRL+HOME and CTRL+END behave the same, regardless of the current selection:
      if (event.ctrlKey) {
        if (event.keyCode === 36) { // CTRL+HOME
          const $firstChild = $(this.editor).children().first();
          if ($firstChild.is('content-block')) {
            this.selectBlock($firstChild[0]);
          } else {
            this.placeSelectionCaretAtStart($firstChild[0]);
          }
          event.preventDefault();
          this.ensureElementIsVisible($firstChild[0]);
          return;
        } if (event.keyCode === 35) { // CTRL+END
          const $lastChild = $(this.editor).children('.bulb-content-trailer').prev();
          if ($lastChild.is('content-block')) {
            this.selectBlock($lastChild[0]);
            $(this.editor).trigger('keypress');
          } else {
            this.placeSelectionCaretAtEnd($lastChild[0]);
          }
          event.preventDefault();
          this.ensureElementIsVisible($lastChild[0]);
          return;
        }
      }

      if (!selectionKeyCodes.includes(event.keyCode)) {
        // non-navigation key codes are all handled normally
        return;
      }

      delete this.$preNavElement;

      if (this.selection) {
        // Handle keys when a block is selected
        this.selectedBlockKeydown(event);
        return;
      }

      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      if ($focusNode.closest('.bulb-edit-caption').length) {
        // The selection is inside a caption, so handle it specially:
        this.captionKeydown(event);
        return;
      }

      // Selection is not a block or a caption, so it must be normal text:
      this.textKeydown(event);
    },

    handleKeyup: function handleKeyup(event) {
      // It is very difficult to determine if the caret is on the first or last line of a paragraph in order to
      // handle an up arrow and down arrow correctly.  Instead, go ahead and let the normal navigation occur, and
      // if we see that the element changes, then we know that changing the selection is the correct thing to do.
      // At that point, handle the selection as appropriate, based on the previous caret location.
      if (this.$preNavElement) {
        const { $preNavElement } = this;

        const selection = this.document.getSelection();
        const $focusNode = $(selection.focusNode);
        let $currentElement;
        let $nextElement;

        if ($preNavElement.is('.bulb-edit-caption')) {
          $currentElement = $focusNode.closest('.bulb-page-content > *');
          if ($preNavElement.closest($currentElement).length === 0) {
            // Was previously in a caption, but now is in another element, so we know we need to navigate:
            if (event.keyCode === 38) { // up arrow
              if (!event.shiftKey) {
                // Not doing a multi-select, so we can simply select the content-block which this
                // caption is a part of
                this.selectBlock($preNavElement.closest('content-block')[0]);
              } else {
                // We don't allow a multi-select to go beyond the caption, so restore the selection to
                // what it was before the keyboard navigation.
                this.base.restoreSelection();
              }
            } else { // down arrow
              $nextElement = $preNavElement.next();
              if ($nextElement.is('content-block')) {
                // We don't allow multi-select between blocks, only select the next block if we
                // aren't ina multi-select.
                if (!event.shiftKey) {
                  this.selectBlock($nextElement[0]);
                } else {
                  this.base.restoreSelection();
                }
              } else if (event.shiftKey) {
                // The next element isn't a content block, so we can just navigate, unless it is a
                // multi-select, as we don't allow the selection to span the caption and subsequent
                // text
                this.base.restoreSelection();
              }
            }
          }
          event.preventDefault();
        } else {
          $currentElement = $focusNode.closest('.bulb-page-content > p');

          if (!$preNavElement.is($currentElement)) {
            if (event.keyCode === 38) { // up arrow
              const $prevElement = $preNavElement.prev();
              if ($prevElement.is('content-block')) {
                // Because the previous element is a block, if we are in a multi-select, restore
                // the previous selection to prevent it from including a block.  Otherwise, if the
                // block doesn't have a caption, select the block.  We don't have to do anything if
                // the block does have a caption, as the default navigation will have already given it
                // focus.
                if (event.shiftKey) {
                  this.base.restoreSelection();
                } else {
                  const $captionElement = $prevElement.find('.bulb-edit-caption');
                  if ($captionElement.length === 0) {
                    this.selectBlock($prevElement[0]);
                  }
                }
              }
            } else { // down arrow
              $nextElement = $preNavElement.next();
              if ($nextElement.is('content-block')) {
                // We always restore the previous selection here, because if a multi-select is taking
                // place, we don't want to allow it to try to include the block.  Alternatively, we
                // want to restore the selection if we select the next block, otherwise the caption
                // for the next block is left with focus.
                this.base.restoreSelection();

                if (!event.shiftKey) {
                  // Only select the block if not part of a multi-select.
                  this.selectBlock($nextElement[0]);
                }
              }
            }
            event.preventDefault();
          }
        }
      }

      if (selectionKeyCodes.includes(event.keyCode)) {
        this.updateButtons();
      }
    },

    placeSelectionCaretAtStart: function placeSelectionCaretAtStart(element) {
      this.unselectBlock();
      MediumEditor.selection.moveCursor(this.document, element, 0);
      this.ensureElementIsVisible(element);
    },

    placeSelectionCaretAtEnd: function placeSelectionCaretAtEnd(element) {
      this.unselectBlock();
      const range = this.document.createRange();
      range.selectNodeContents(element);
      range.collapse(false); // false collapses the selection at the end, rather than the start
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      this.ensureElementIsVisible(element);
    },

    ensureElementIsVisible: function ensureElementIsVisible(element) {
      const rect = element.getBoundingClientRect();
      const isInViewport = (
        rect.top >= 0
        && rect.left >= 0
        && rect.bottom <= (window.innerHeight || this.document.documentElement.clientHeight)
        && rect.right <= (window.innerWidth || this.document.documentElement.clientWidth)
      );

      if (!isInViewport) {
        element.scrollIntoView();
      }
    },

    textKeydown: function textKeydown(event) {
      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      const $ancestorElement = $focusNode.closest('.bulb-page-content > *');
      let offset = { left: null, right: null };

      if ($ancestorElement.length !== 0) {
        offset = MediumEditor.selection.getCaretOffsets($ancestorElement[0]);
      }

      const $prevElement = $ancestorElement.prev();
      const $nextElement = $ancestorElement.next();

      switch (event.keyCode) {
        case 37: // Left Arrow
          if (offset.left === 0 && $prevElement.is('content-block')) {
            const $captionElement = $prevElement.find('.bulb-edit-caption');
            if ($captionElement.length) {
              this.placeSelectionCaretAtEnd($captionElement[0]);
            } else {
              this.selectBlock($prevElement[0]);
            }
            event.preventDefault();
          } else if (offset.left === 0 && !$prevElement[0]) {
            // page top
            this.focusPageTitle();
            event.preventDefault();
          }
          break;

        case 39: // Right Arrow
          if (offset.right === 0 && $nextElement.is('content-block')) {
            this.selectBlock($nextElement[0]);
            event.preventDefault();
          }
          break;

        case 38: // Up Arrow
          if (!$prevElement[0]) {
            // page top
            this.focusPageTitle(offset.left);
            event.preventDefault();
          }
          break;

        case 40: // Down Arrow
          this.base.saveSelection();
          this.$preNavElement = $ancestorElement;
          break;

        case 8: // Backspace
          // This is the beginning of the page.
          if (offset.left === 0 && ($prevElement.is('content-block') || !$prevElement[0])) {
            // If at the start of a paragraph following a block or with no previous element,
            // backspace does nothing.
            event.preventDefault();
          }
          break;

        case 46: // Delete
          if (offset.right === 0
            && offset.left !== 0
            && selection.isCollapsed
            && $nextElement.is('content-block')) {
            // at the end of a non-empty paragraph, before a block, and there isn't a selection, delete
            // does nothing.
            event.preventDefault();
          }
          break;
        // TODO DESIGN : how to handle pgup and pgdown?
      }

      this.updateButtons();
    },

    selectedBlockKeydown: function selectedBlockKeydown(event) {
      event.preventDefault();

      if (event.shiftKey) {
        // We don't allow multi-select with blocks in it, so we can just ignore any navigation from a block
        // if the shiftKey is down
        return;
      }

      const $selectedBlock = this.contentEditorCtrl.selectedBlockElement; // TODO CLEAN fix naming convention
      const $prevElement = $selectedBlock.prev();
      const $nextElement = $selectedBlock.next();
      let $captionElement;

      switch (event.keyCode) {
        case 37: // Left Arrow
        case 38: // Up Arrow
          if ($prevElement.is('content-block')) {
            $captionElement = $prevElement.find('.bulb-edit-caption');
            if ($captionElement.length) {
              this.placeSelectionCaretAtEnd($captionElement[0]);
            } else {
              this.selectBlock($prevElement[0]);
            }
          } else if ($prevElement) {
            this.placeSelectionCaretAtEnd($prevElement[0]);
          } else {
            // page top
            this.focusPageTitle();
            event.preventDefault();
          }
          break;

        case 39: // Right Arrow
        case 40: // Down Arrow
          $captionElement = $selectedBlock.find('.bulb-edit-caption');
          if ($captionElement.length !== 0) {
            this.placeSelectionCaretAtStart($captionElement[0]);
          } else if ($nextElement.is('content-block')) {
            this.selectBlock($nextElement[0]);
          } else if ($nextElement.is('p')) {
            this.placeSelectionCaretAtStart($nextElement[0]);
          } else {
            event.preventDefault();
          }
          break;

        case 13: // Enter
          let $newElement;
          if (event.ctrlKey) {
            $selectedBlock.before('<p><br/></p>');
            $newElement = $selectedBlock.prev();
          } else {
            $selectedBlock.after('<p><br/></p>');
            $newElement = $selectedBlock.next();
          }
          this.unselectBlock();
          MediumEditor.selection.select(this.document, $newElement[0], 0);
          break;

        case 8: // Backspace
        case 46: // Delete
          this.deleteSelectedBlock();
          break;

        // TODO DESIGN : how to handle pgup and pgdown?
      }

      this.updateButtons();
    },

    captionKeydown: function captionKeydown(event) {
      // TODO PERFORM : figure out how to gather these values only if a nav key is hit, rather than for every key.
      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      const $captionElement = $focusNode.closest('.bulb-edit-caption');
      const $block = $focusNode.closest('content-block');
      const offset = MediumEditor.selection.getCaretOffsets($captionElement[0]);

      switch (event.keyCode) {
        case 37: // Left Arrow
          if (offset.left === 0) {
            event.preventDefault();
            this.selectBlock($block[0]);
          }
          // otherwise, handle as normal
          break;

        case 39: // Right Arrow
          if (offset.right === 0) {
            const $nextElement = $block.next();
            if ($nextElement.is('content-block')) {
              this.selectBlock($nextElement[0]);
              event.preventDefault();
            }
          }
          // otherwise, just handle as normal
          break;

        case 38: // Up Arrow
        case 40: // Down Arrow
          this.base.saveSelection();
          this.$preNavElement = $captionElement;
          break;

        case 13: // Enter
          this.base.saveSelection();
          this.$preNavElement = $captionElement;

          const that = this;
          this.appendNewLine($(selection.focusNode).closest('content-block')).then((node) => {
            const $nextNode = node.next();
            $timeout(() => {
              that.placeSelectionCaretAtStart($nextNode[0]);
            });
          });

          event.preventDefault();
          break;

        case 8: // Backspace
          // If at beginning of caption, do nothing, else handle normally
          if (offset.left === 0) {
            event.preventDefault();
          }
          break;

        case 46: // Delete
          // if at end of caption, do nothing, else handle normally
          if (offset.right === 0) {
            event.preventDefault();
          }
          break;

        // TODO DESIGN : how to handle pgup and pgdown?
      }

      this.updateButtons();
    },

    selectBlock: function selectBlock(element) {
      const $element = $(element);
      // First, go up the heirarchy, looking for a 'selectable-block' element.
      let $selectableElement = $element.closest('[selectable-block]');
      if ($selectableElement.length === 0) {
        // Didn't find one going up, so try going down:
        $selectableElement = $element.find('[selectable-block]');
      }
      if ($selectableElement.length === 0) {
        // Didn't find a selectable element in either direction.
        // TODO CLEAN - do nothing, or select null?
        return;
      }

      $(this.editor).find('.selected-block').removeClass('selected-block');
      $selectableElement.addClass('selected-block'); // TODO CLEAN : improve name of this

      this.selection = $selectableElement[0];

      // MediumEditor.selection.selectNode uses selectNodeContents, so we really want to select the parent
      // of the content, which means we want to select the ng-transclude node:
      const transcludeNode = $selectableElement.children('[ng-transclude]')[0];

      // This is almost the same as MediumEditor's selectElement() api, but this is smarter
      // as it doesn't do a focus if the element is already has focus.  Doing such a focus sometimes causes
      // the selection to be lost.
      MediumEditor.selection.selectNode(transcludeNode, this.document);
      const selectedEditor = MediumEditor.selection.getSelectionElement(this.window);
      const focusedElement = this.base.getFocusedElement();
      if (selectedEditor !== focusedElement) {
        this.base.events.focusElement(selectedEditor);
      }

      this.ensureElementIsVisible($selectableElement[0]);
      this.blockSelected($selectableElement.closest('content-block')[0]);
      this.updateButtons();
    },

    handleBlur: function handleBlur(event, element) {
      $(element).find('.bulb-media-active').removeClass('bulb-media-active');
      this.hideButtons(element);
      $(this.editor).find('.selected-block').removeClass('selected-block');
      this.blockSelected(null);
      this.selection = null;
    },

    handleInput: function handleInput(/* event, element */) {
      this.updateButtons();

      // remove duplicate id
      const selection = this.document.getSelection();
      const $focusNode = $(selection.focusNode);
      const $pElement = $focusNode.closest('.bulb-page-content > *');

      if ($pElement) {
        const id = $pElement.attr('id');
        const count = $(`[id=${id}]`).length;
        if (count !== 1) {
          // duplicate id
          BulbCommentService.getParagraphIds(1).then((paragraphIds) => {
            $pElement.attr('id', paragraphIds[0]);
          });
        }
      }
    },

    updateButtons: function updateButtons() {
      if (!this.buttons) {
        this.editor.appendChild(this.getButtonsElement());
      }

      $(this.editor).find('.bulb-media-active').removeClass('bulb-media-active');

      const selection = this.window.getSelection();
      // if there isn't a selection, then don't show any buttons
      if (!selection || selection.rangeCount === 0) {
        this.hideButtons();
        return;
      }

      const range = selection.getRangeAt(0);
      let current = range.commonAncestorContainer;
      current = this.getElementAncestor(current);

      // the selection is outside the editor is selected, so hide the buttons
      if (current === null) {
        this.hideButtons();
        return;
      }

      // Only show the buttons if the current selection is an editable element AND the immediate child of the
      // editor AND empty.
      const $current = $(current);
      if (current.isContentEditable && current.parentElement === this.editor && $current.text().trim() === '') {
        this.showButtons(current);
        $current.addClass('bulb-media-active');
      } else {
        this.hideButtons();
      }
    },

    showButtons: function showButtons(element) {
      this.positionButtons(element);
      this.buttons.classList.remove('hide');

      const $options = $(this.buttons).find('.bulb-insert-block-buttons-options');
      $options.addClass('hide');
    },

    hideButtons: function hideButtons() {
      this.buttons.classList.add('hide');
      $(this.buttons).find('.bulb-insert-block-buttons-show').removeClass('bulb-insert-block-button-rotate');
      $(this.editor).find('.bulb-media-active').removeClass('bulb-media-active');
    },

    getButtonsElement: function getButtonsElement() {
      if (!this.buttons) {
        this.buttons = this.createButtons();
      }

      return this.buttons;
    },

    createButtons: function createButtons() {
      // TODO POLISH : add tooltips

      const buttons = this.document.createElement('div');
      buttons.id = `bulb-insert-block-buttons-${this.getEditorId()}`;
      buttons.setAttribute('class', 'bulb-insert-block-buttons bulb-media-transient hide');
      buttons.setAttribute('contentEditable', false);

      const videoButton = this.document.createElement('button');
      videoButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
      videoButton.setAttribute('tabindex', '0');
      videoButton.innerHTML = `<span class="button-icon fas fa-video"></span>${
        $translate.instant('content_block_type_VIDEO')}`;
      buttons.appendChild(videoButton);
      this.on(videoButton, 'click', this.handleVideoButtonClick.bind(this));
      this.on(videoButton, 'focus', this.deselectAll.bind(this));

      const imageButton = this.document.createElement('button');
      imageButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
      imageButton.setAttribute('tabindex', '0');
      imageButton.innerHTML = `<span class="button-icon far fa-image"></span>${
        $translate.instant('content_block_type_IMAGE')}`;
      buttons.appendChild(imageButton);
      this.on(imageButton, 'click', this.handleMediaButtonClick.bind(this));
      this.on(imageButton, 'focus', this.deselectAll.bind(this));

      if (BulbAuthService.currentUser.features.AddFileFeature.enabled) {
        const attachmentButton = this.document.createElement('button');
        attachmentButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
        attachmentButton.setAttribute('tabindex', '0');
        attachmentButton.innerHTML = `<span class="button-icon fa fa-paperclip"></span>${
          $translate.instant('content_block_type_FILE')}`;
        buttons.appendChild(attachmentButton);
        this.on(attachmentButton, 'click', this.handleAttachmentButtonClick.bind(this));
        this.on(attachmentButton, 'focus', this.deselectAll.bind(this));
      }

      if (BulbAuthService.currentUser.features.AudioFeature.enabled) {
        const audioButton = this.document.createElement('button');
        audioButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
        audioButton.setAttribute('tabindex', '0');
        audioButton.innerHTML = `<span class="button-icon fas fa-music"></span>${
          $translate.instant('content_block_type_AUDIO')}`;
        buttons.appendChild(audioButton);
        this.on(audioButton, 'click', this.handleAudioButtonClick.bind(this));
        this.on(audioButton, 'focus', this.deselectAll.bind(this));
      }
      if (BulbAuthService.currentUser.features.AddEmbedFeature.enabled) {
        const embedButton = this.document.createElement('button');
        embedButton.setAttribute('class', 'bulb-insert-block-buttons-media aero-button bulb-aero-blue-button');
        embedButton.setAttribute('tabindex', '0');
        embedButton.innerHTML = `<span class="button-icon fa fa-sign-in"></span>${
          $translate.instant('content_block_type_URL')}`;
        buttons.appendChild(embedButton);
        // Unfortunately click event is not firing on Enter key press for this button which
        // is happening for other buttons above.On adding the keydown event explicitly
        // it is surprisingly opening modal twice.So to fix it we are ignoring keydown event in
        // handleEmbedButtonClick method.
        this.on(embedButton, 'keydown', this.handleEmbedButtonClick.bind(this));
        this.on(embedButton, 'click', this.handleEmbedButtonClick.bind(this));
        this.on(embedButton, 'focus', this.deselectAll.bind(this));
      }

      return buttons;
    },

    deselectAll: function deselectAll(/* event */) {
      window.getSelection().removeAllRanges();
    },

    positionButtons: function positionButtons() {
      const selection = this.window.getSelection();
      let range;
      let current;
      if (!selection || selection.rangeCount === 0) {
        current = this.base.getFocusedElement();
      } else {
        range = selection.getRangeAt(0);
        current = range.commonAncestorContainer;
      }

      current = this.getElementAncestor(current);

      this.buttons.style.top = `${current.offsetTop}px`;
    },

    getElementAncestor: function getElementAncestor(element) {
      // if (element === this.editor) {
      //
      //   var editorElementChildren = $('.bulb-page-content').children();
      //   for (var i = 0; i < editorElementChildren.length; i++) {
      //
      //     var currentChild = $(editorElementChildren[i]);
      //     if (currentChild.prop('tagName').toLowerCase() !== 'p' &&
      //       currentChild.prop('tagName').toLowerCase() !== 'content-block') {
      //
      //       if (currentChild.prop('tagName').toLowerCase() === 'div' &&
      //         currentChild.attr('class').indexOf('bulb-insert-block-buttons') >= 0) {
      //         break;
      //       }
      //
      //       var siblingChild = currentChild.prev().get(0);
      //       currentChild.replaceWith();
      //       this.placeSelectionCaretAtEnd(siblingChild);
      //     }
      //   }
      // }

      // for firefox
      // The following result is different between firefox and other browsers.
      // 'selection.getRangeAt(0).commonAncestorContainer'
      if (element === this.editor) {
        return $(element).children().eq(0)[0];
      }

      if (!element || element.parentElement === this.editor) {
        return element;
      }
      return this.getElementAncestor(element.parentNode);
    },

    handleShowButtonClick: function handleShowButtonClick(event) {
      const $options = $(this.buttons).find('.bulb-insert-block-buttons-options');
      if ($options.hasClass('hide')) {
        $options.removeClass('hide');
        $(this.buttons).find('.bulb-insert-block-buttons-show').addClass('bulb-insert-block-button-rotate');
      } else {
        $options.addClass('hide');
        $(this.buttons).find('.bulb-insert-block-buttons-show').removeClass('bulb-insert-block-button-rotate');
      }
      event.stopPropagation();
    },

    initContentBlock: function initContentBlock($place, tempId) {
      this.hideButtons();

      $place.attr('id', tempId);
      $place.addClass('bulb-media-temp-placeholder');
      if (this.isLastContent($place)) {
        this.appendNewLine($place);
      }
    },

    $lastPlace: null,

    /**
     * Add callback function reference to handle the initial upload process.
     *
     * @param event Current Event.
     * @param data File data for upload.
     */
    addFileHandler: function addFileHandler(event, data) {
      // eslint-disable-next-line no-param-reassign
      data.tempId = this.generateTemporaryId();
      BulbAssetLibraryService.dialog.dismissAllModals();
    },

    /**
     * Callback function to alert the submission of an asset for upload process.
     *
     * @param event Current event.
     * @param data File data for upload.
     * @param fnCallBack Provided callback to invoke.
     */
    submitFileHandler: function submitFileHandler(event, data, fnCallBack) {
      const that = this;

      const uploadStartedEvent = new CustomEvent('assetLibraryUploadStarted', {
        detail: {
          uploadData: data,
          page: that.page,
        },
      });

      BulbCKEStateService.assetLibraryUploadEvent.next(uploadStartedEvent);

      // Need to emulate the sequential callback invocation with our normal upload workflow.
      // This will only ever get hit on third party integrations like google-drive, google-photo and MS OneDrive.
      if (fnCallBack) {
        let intervalCounter = 10;
        const externalIntegrationProgressInterval = setInterval(() => {
          intervalCounter += 10;
          that.progressFileHandler(event, data, intervalCounter);
        }, 1000);
        $timeout(() => {
          fnCallBack(event, data);
          clearInterval(externalIntegrationProgressInterval);
        }, 6000);
      }
    },

    /**
     * show busy indicator during asset upload.
     */
    showBusyIndicator: function showBusyIndicator() {
      let stopBusyIndicatorCallback = null;
      stopBusyIndicatorCallback = BulbBusyCoverService.showBusy();
      BulbCKEStateService.stopBusyIndicator.next(stopBusyIndicatorCallback);
    },

    /**
     *
     * @param assetType Type of asset.
     * Called on asset success uploads.
     */
    assetSuccessHandler: function assetSuccessHandler(assetType) {
      const assetEvents = AssetUploadEventService.uppyEventEmitter.subscribe((event) => {
        if (event.status === 'PROCESSING_SUCCESS' && event.uploadState === 'page') {
          const that = this;
          if (BulbCKEStateService.uploadToServer.value) {
            const hideBusy = BulbBusyCoverService.showBusy();
            BulbCKEStateService.uploadToServer.next(false);
            const uploadSub = AssetUploadHttpService
              .fetchAssetLibraryAsset(event.response.entry.id, assetType === 'audio' || assetType === 'video')
              .subscribe((asset) => {
                if (asset.entry.status === 'COMPLETE' || asset.entry.status === 'PROCESSING') {
                  if (assetType === 'file' || assetType === 'image') {
                    that.showBusyIndicator();
                  }
                  if (BulbCKEStateService.getDesktopUploadSelectionMode()) {
                    if (assetType === 'audio' || assetType === 'video') {
                      that.showBusyIndicator();
                    }
                    BulbCKEStateService.setDesktopUploadSelectionMode(false);
                    that.doneFileHandler(null, asset, assetType !== 'image', event.response.nextIndex);
                  } else {
                    that.doneFileHandler(null, asset, false);
                  }
                } else if (asset.entry.status === 'ERROR' && assetType === 'image') {
                  AeroDialogService.showDialog({
                    icon: 'fa fa-exclamation-triangle',
                    iconClass: 'aero-alert',
                    title: $translate.instant('modal_oops_title'),
                    detail: $translate.instant('modal_image_heic_warning'),
                    primaryLabel: $translate.instant('button_ok'),
                    closeOnPrimary: true,
                    showSecondary: false,
                  });
                }
                hideBusy();
                uploadSub.unsubscribe();
              });
          }
          if (!event.response.hasMoreFiles) {
            assetEvents.unsubscribe();
          }
        }
      });
    },

    /**
     * Progress callback function reference.
     *
     * @param event Current Event.
     * @param data File data for upload.
     * @param progress Current overall progress of file upload.
     */
    progressFileHandler: function progressFileHandler(event, data, progress) {
      const customUploadProgressEvent = new CustomEvent('assetLibraryUploadProgress', {
        detail: {
          data,
          progress,
        },
      });
      $window.dispatchEvent(customUploadProgressEvent);
    },

    /**
     * Done callback function reference to handle the completion of asset upload.
     *
     * @param event Current Event.
     * @param data File data for upload.
     * @param wasUploadedToBulb If the asset was originally uploaded to bulb.
     */
    doneFileHandler: function doneFileHandler(event, data, wasUploadedToBulb, nextIndex) {
      BulbCKEStateService.uploadToServer.next(true);
      const that = this;
      const entryId = data.entry.id;

      // Can't use ES6 to set a default value in the parameters, so do the same thing here.
      if (typeof wasUploadedToBulb === 'undefined') {
        // eslint-disable-next-line no-param-reassign
        wasUploadedToBulb = true;
      }

      BulbAssetLibraryService.api.getEntry(entryId).then((res) => {
        const { entry } = res;
        switch (entry.type) {
          case 'ATTACHED_FILE':
            that.createAttachedFileBlock(entry, wasUploadedToBulb);
            break;
          case 'AUDIO':
            that.createAudioBlock(entry, wasUploadedToBulb, data.isRecordedAudio);
            break;
          case 'IMAGE':
            that.createImageBlock(entry, wasUploadedToBulb, nextIndex);
            break;
          case 'VIDEO':
            that.createVideoBlock(entry, wasUploadedToBulb);
            break;
        }
      });
    },

    /**
     * This function is used to handle error when file is unable to process.
     * @param data
     */
    errorFileHandler: function errorFileHandler(data) {
      const $contentBlockElement = $(`#${data.entry.tempId}`);
      $contentBlockElement.empty();
      delete this.page.unit.contentBlocks[data.entry.tempId];
    },

    /**
     * Function that handles the specific workflow for adding an image block to the current page.
     *
     * @param event
     */
    handleMediaButtonClick: function handleMediaButtonClick(event) {
      event.stopPropagation();
      BulbCKEStateService.assetType.next('IMAGE');
      UppyDashboardService.open({
        assetLibrary: true,
        multiSelect: true,
        allowedFileTypes: uppyAllowedImageFileTypes,
        uploadState: 'page',
      });
      this.assetSuccessHandler('image');
    },

    /**
     * Function that handles the specific workflow for adding a video block onto the current page.
     *
     * @param event
     */
    handleVideoButtonClick: function handleVideoButtonClick(event) {
      event.stopPropagation();

      BulbCKEStateService.assetType.next('VIDEO');
      UppyDashboardService.open({
        assetLibrary: true,
        multiSelect: false,
        allowedFileTypes: uppyAllowedVideoFileTypes,
        uploadState: 'page',
        embed: true,
      });
      this.assetSuccessHandler('video');
      const uploadSub = AssetUploadEventService.openEmbedModal.subscribe((response) => {
        uploadSub.unsubscribe();
        this.handleEmbedButtonClick(event);
      });
    },

    /**
     * Function that handles the specific workflow for adding an audio block onto the current page.
     *
     * @param event
     */
    handleAudioButtonClick: function handleAudioButtonClick(event) {
      event.stopPropagation();

      BulbCKEStateService.assetType.next('AUDIO');
      UppyDashboardService.open({
        assetLibrary: true,
        multiSelect: false,
        allowedFileTypes: uppyAllowedAudioFileTypes,
        uploadState: 'page',
      });
      this.assetSuccessHandler('audio');
    },

    handleAttachmentButtonClick: function handleAttachmentButtonClick(event) {
      event.stopPropagation();
      BulbCKEStateService.assetType.next('attached_file');
      UppyDashboardService.open({
        assetLibrary: true,
        multiSelect: false,
        uploadState: 'page',
      });
      this.assetSuccessHandler('file');
    },

    handleEmbedButtonClick: function handleEmbedButtonClick(event) {
      event.stopPropagation();

      const that = this;
      let modalInstance;

      const modalOptions = {
        templateUrl: 'ajs/components/contenteditor/embedblockform.html',
        closeOnClick: true,
        controller: ['$scope', function embedBlockFormModalController($scope) {
          /**
           * This function handles the paste event for MS Edge.
           *
           * @param pasteEvent The original event.
           * @param form The embedForm that we use to create the embed block.
           */
          $scope.handlePaste = function handlePaste(pasteEvent, form) {
            // Only add this to users that are using MS Edge or FireFox, as to how they handle past events.
            if (BulbBrowserService.isMsEdge() || BulbBrowserService.isFireFox()) {
              pasteEvent.preventDefault();

              // We want to be explicit of where we paste the content
              const $embedBlockFormURLInput = $('#url');

              // Grab the data from the clipboard
              const pasteData = (pasteEvent.originalEvent || pasteEvent).clipboardData.getData('text/plain');

              // Append the data to the input field
              $embedBlockFormURLInput.val(pasteData);

              // Need to update the state of the form manually.
              form.$setDirty();
              form.$invalid = false;
              form.$valid = true;

              // Pass the value of the url that the user pasted to the embedBlockCtrl.
              $scope.embedBlockFormCtrl.url = pasteData;
            }
          };

          this.submitUrl = function submitUrl(form) {
            const { url } = $scope.embedBlockFormCtrl;

            // If embed URL is a Vimeo or YouTube URL, handle with asset library service
            if (that.isYouTubeOrVimeoUrl(url)) {
              that.handleYouTubeAndVimeoVideoEmbed(url);
            } else {
              let fileId;

              if (url.indexOf('docs.google.com') !== -1) {
                // get fileId from url.
                const parts = url.split('d/');
                fileId = parts[1].split('/')[0];
              }
              if (url.indexOf('1drv.ms') > -1) {
                BulbAssetLibraryOneDriveService.getSharedEmbedUrl(url).then((response) => {
                  $scope.embedBlockFormCtrl.url = response;
                  that.createEmbedBlock.call(
                    that,
                    form,
                    $scope.embedBlockFormCtrl.url,
                    modalInstance,
                    null,
                    fileId,
                    null,
                  );
                }, (error) => {
                  BulbAssetLibraryOneDriveService.showEmbedError({
                    fontAwesomeCode: 'fas fa-clouds',
                    titleCode: 'embed_onedrive_url_error_title',
                    bodyCode: error,
                    showCancelButton: false,
                    windowClass: 'updated-modal-design bulb-aero-modal',
                  });
                });
              } else if (url.indexOf('onedrive.live.com') > -1) {
                BulbAssetLibraryOneDriveService.createSharableLinkForDocument(url)
                  .then((response) => {
                    $scope.embedBlockFormCtrl.url = response;
                    that.createEmbedBlock.call(
                      that,
                      form,
                      $scope.embedBlockFormCtrl.url,
                      modalInstance,
                      null,
                      fileId,
                      null,
                    );
                  }, (error) => {
                    BulbAssetLibraryOneDriveService.showEmbedError({
                      fontAwesomeCode: 'fas fa-clouds',
                      titleCode: 'embed_onedrive_url_error_title',
                      bodyCode: (error.data) ? error.data.message : error,
                      showCancelButton: false,
                      windowClass: 'updated-modal-design bulb-aero-modal',
                    });
                  });
              } else if (url.indexOf('sharepoint.com') > -1) {
                /**
                 * OneDrive for business does not support embeds, so showing error if the shared url
                 * belongs to OneDrive business account.
                 */
                BulbAssetLibraryOneDriveService.showEmbedError({
                  fontAwesomeCode: 'fas fa-exclamation-triangle',
                  titleCode: 'embed_onedrive_account_not_supported_title',
                  cancelButtonCode: 'onboarding_got_it_button',
                  bodyCode: 'embed_onedrive_account_not_supported',
                  showCancelButton: true,
                  showOkButton: false,
                  windowClass: 'updated-modal-design bulb-aero-modal bulb-onedrive-embed-modal',
                });
              } else {
                that.createEmbedBlock.call(
                  that,
                  form,
                  $scope.embedBlockFormCtrl.url,
                  modalInstance,
                  null,
                  fileId,
                  null,
                );
              }
            }
          };
        }],
        controllerAs: 'embedBlockFormCtrl',
        scope: this.scope,
        backdrop: true,
        windowClass: 'bulb-media-transient',
      };

      // Add this to the parent element so that you can't resize the dialog on IE.
      $modal.open(modalOptions);
    },

    handleURLPaste: function handleURLPaste(event) {
      event.stopPropagation();

      const that = this;
      const url = event.detail.url;

      // If embed URL is a Vimeo or YouTube URL, handle with asset library service
      if (that.isYouTubeOrVimeoUrl(url)) {
        that.handleYouTubeAndVimeoVideoEmbed(url, true);
      } else {
        let fileId;

        if (url.indexOf('docs.google.com') !== -1) {
          // get fileId from url.
          const parts = url.split('d/');
          fileId = parts[1].split('/')[0];
        }
        if (url.indexOf('1drv.ms') > -1) {
          BulbAssetLibraryOneDriveService.getSharedEmbedUrl(url).then((response) => {
            $scope.embedBlockFormCtrl.url = response;
            that.createEmbedBlock.call(
              that,
              null,
              url,
              null,
              null,
              fileId,
              null,
              true
            );
          }, (error) => {
            BulbAssetLibraryOneDriveService.showEmbedError({
              fontAwesomeCode: 'fas fa-clouds',
              titleCode: 'embed_onedrive_url_error_title',
              bodyCode: error,
              showCancelButton: false,
              windowClass: 'updated-modal-design bulb-aero-modal',
            }).then(() => {
              const embedURLErrorEvent = new CustomEvent('embedURLErrorEvent', {});
              window.dispatchEvent(embedURLErrorEvent);
            });
          });
        } else if (url.indexOf('onedrive.live.com') > -1) {
          BulbAssetLibraryOneDriveService.createSharableLinkForDocument(url)
            .then((response) => {
              that.createEmbedBlock.call(
                that,
                null,
                url,
                null,
                null,
                fileId,
                null,
                true
              );
            }, (error) => {
              BulbAssetLibraryOneDriveService.showEmbedError({
                fontAwesomeCode: 'fas fa-clouds',
                titleCode: 'embed_onedrive_url_error_title',
                bodyCode: (error.data) ? error.data.message : error,
                showCancelButton: false,
                windowClass: 'updated-modal-design bulb-aero-modal',
              }).then(() => {
                const embedURLErrorEvent = new CustomEvent('embedURLErrorEvent', {});
                window.dispatchEvent(embedURLErrorEvent);
              });
            });
        } else if (url.indexOf('sharepoint.com') > -1) {
          /**
           * OneDrive for business does not support embeds, so showing error if the shared url
           * belongs to OneDrive business account.
           */
          BulbAssetLibraryOneDriveService.showEmbedError({
            fontAwesomeCode: 'fas fa-exclamation-triangle',
            titleCode: 'embed_onedrive_account_not_supported_title',
            cancelButtonCode: 'onboarding_got_it_button',
            bodyCode: 'embed_onedrive_account_not_supported',
            showCancelButton: true,
            showOkButton: false,
            windowClass: 'updated-modal-design bulb-aero-modal bulb-onedrive-embed-modal',
          }).catch(() => {
            const embedURLErrorEvent = new CustomEvent('embedURLErrorEvent', {});
            window.dispatchEvent(embedURLErrorEvent);
          });
        } else {
          that.createEmbedBlock.call(
            that,
            null,
            url,
            null,
            null,
            fileId,
            null,
            true
          );
        }
      }
    },

    showImagePreview: function showImagePreview(img, data) {
      const {tempId} = data.entry;
      const $place = $(this.editor).find(`#${tempId}`);

      // Create temporary uploading image block:
      // Add the block to the unit blocks:
      this.page.unit.contentBlocks[tempId] = {
        id: tempId,
        img,
        blockType: 'UPLOADING_IMAGE',
        data,
      };

      // TODO CLEAN - use a template rather than embedded string.
      // Swap in a content-block element that points to the new temporary block:
      $place.replaceWith($compile(`<content-block id="${tempId
      }" contenteditable="false" class="bulb-media-transient align-center"></content-block>`)(this.scope));
      // this.scope.$apply();
      // add newline after content block
      this.appendNewLine($(this.editor).find(`#${tempId}`));
      // this.selectBlock($(this.editor).find('#' + data.tempId)[0]);

      // TODO DESIGN : what about changing the cursor to a busy cursor during the upload and processing?
      // or would that be confusing since you can still interact in order to cancel the upload?
    },

    showUploadingFileProgressbar: function showUploadingFileProgressbar(data) {
      const { tempId } = data.entry;
      const $place = $(this.editor).find(`#${tempId}`);

      // Create temporary uploading file block:
      // Add the block to the unit blocks:
      this.page.unit.contentBlocks[tempId] = {
        id: tempId,
        blockType: 'UPLOADING_FILE',
        data,
        progress: 0,
      };

      // TODO CLEAN - use a template rather than embedded string.
      // Swap in a content-block element that points to the new temporary block:
      $place.replaceWith($compile(`<content-block id="${tempId
      }" contenteditable="false" class="bulb-media-transient align-center"></content-block>`)(this.scope));

      // this.scope.$apply();

      this.selectBlock($(this.editor).find(`#${tempId}`)[0]);
      // add newline after content block
      this.appendNewLine($(this.editor).find(`#${tempId}`));
      // TODO DESIGN : what about changing the cursor to a busy cursor during the upload and processing?
      // or would that be confusing since you can still interact in order to cancel the upload?
    },

    updateId: function updateId(data) {
      const $place = $(this.editor).find(`#${data.tempId}`);
      $place.attr('id', data.result.id);
      this.trigger('editableInput', null, this.editor);
    },

    cancelUpload: function cancelUpload(event, contentBlock) {
      const that = this;
      // TODO ISSUE : confirm cancel
      contentBlock.data.jqXHR.abort();
      delete this.page.unit.contentBlocks[contentBlock.id];
      const $place = $(this.editor).find(`#${contentBlock.id}`);
      createNewParagraphData().then((data) => {
        $place.replaceWith(data);
        // TODO ISSUE : update the selection to the empty paragraph were we were uploading to

        // NOTE: We need to explicitly hide the toolbar when a user aborts the upload process of a content-block
        // Due to how the implementation of medium-editor's 'updateToolbar' function works. It currently doesn't
        // Handle updating the toolbar if the toolbar has an 'empty' state. We could choose to override this
        // Behaviour in the future to allow the 'updateToolbar' function to handle an empty toolbar state.
        // Another location for which we need to do this is when a user deletes a content-block.
        const toolbar = that.base.getExtensionByName('toolbar');
        $timeout(() => {
          toolbar.hideToolbar();
        }, CONTENT_EDITOR.GRAB_TOOLBAR_TIMEOUT);
      });
    },

    generateTemporaryId: function generateTemporaryId() {
      // Take the current time converted to base 36 to shorten it and append a random string to it.
      return `${Date.now().toString(36)}_${Foundation.utils.random_str(6)}`; // jshint ignore:line
    },

    /**
     * Create image block on page.
     *
     * @param entry Entry to create image block for.
     * @param uploadedToBulb If it was originally uploaded to bulb. Usually local file system.
     */
    createImageBlock: function createImageBlock(entry, uploadedToBulb, nextIndex) {
      const that = this;

      const uploadImageDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadImageDTO, 'imageblock')
          .then((response) => {
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            const eventName = 'assetLibraryImageResponse';
            const customImageEvent = new CustomEvent(eventName, {
              detail: {
                contentBlock: response.contentBlock,
                page: that.page,
                trackingId: entry.id,
                nextIndex,
              },
            });
            $window.dispatchEvent(customImageEvent);
          })
          .catch((err) => {
            $window.dispatchEvent(new CustomEvent('assetLibraryImageErrorResponse'));
            if (err.data.developerMessage && err.data.developerMessage.startsWith('Unable to resize image')) {
              AeroDialogService.showDialog({
                icon: 'fas fa-exclamation-triangle',
                iconClass: 'aero-alert',
                detail: $translate.instant('image_processing_timeout'),
                primaryLabel: $translate.instant('button_ok'),
                showSecondary: false,
              });
            } else {
              AeroDialogService.showDialog({
                icon: 'fa fa-exclamation-triangle',
                iconClass: 'aero-alert',
                title: $translate.instant('modal_oops_title'),
                detail: $translate.instant('modal_image_heic_warning'),
                primaryLabel: $translate.instant('button_ok'),
                closeOnPrimary: true,
                showSecondary: false,
              });
            }
          }),
      });
    },

    /**
     * Create video block on page.
     *
     * @param entry Entry to create image block for.
     * @param uploadedToBulb If it was originally uploaded to bulb. Usually local file system.
     */
    createVideoBlock: function createVideoBlock(entry, uploadedToBulb) {
      const that = this;

      const uploadVideoDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadVideoDTO, 'videoblock')
          .then((response) => {
            // Update content block references on page :
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            const eventName = uploadedToBulb ? 'assetLibraryUploadCompleted' : 'assetLibraryVideoResponse';
            const customVideoEvent = new CustomEvent(eventName, {
              detail: {
                contentBlock: response.contentBlock,
                page: that.page,
                trackingId: entry.id,
              },
            });
            $window.dispatchEvent(customVideoEvent);
          }),
      });
    },

    /**
     * Create Audio block on page.
     *
     * @param entry Entry to create image block for.
     * @param uploadedToBulb If it was originally uploaded to bulb. Usually local file system.
     * @param isRecordedAudio If the audio was recorded through our app or not.
     */
    createAudioBlock: function createAudioBlock(entry, uploadedToBulb, isRecordedAudio) {
      const that = this;

      const uploadAudioDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadAudioDTO, 'audioblock')
          .then((response) => {
            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            if (isRecordedAudio) {
              const audioRecordingEvent = new CustomEvent('audioRecordingDone', {
                detail: {
                  contentBlockId: response.contentBlock.id,
                },
              });
              $window.dispatchEvent(audioRecordingEvent);
            }

            const eventName = uploadedToBulb ? 'assetLibraryUploadCompleted' : 'assetLibraryAudioResponse';
            const customAudioEvent = new CustomEvent(eventName, {
              detail: {
                contentBlock: response.contentBlock,
                page: that.page,
                trackingId: entry.id,
              },
            });
            $window.dispatchEvent(customAudioEvent);
          }),
      });
    },

    /**
     * Create an attached file block on a page.
     *
     * @param entry Entry to create image block for.
     * @param uploadedToBulb If it was originally uploaded to bulb. Usually local file system.
     */
    createAttachedFileBlock: function createAttachedFileBlock(entry, uploadedToBulb) {
      const that = this;

      const uploadAttachedFileDTO = {
        assetLibraryEntryId: entry.id,
      };

      SerialSaveService.save({
        showBusy: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(uploadAttachedFileDTO, 'attachedfileblock')
          .then((response) => {
            // The response will have the embedFailure property when the uploaded file has an extension we can
            // embed, but failed to embed. The purpose of the embedFailure property is to alert the user that
            // we normally embed the file type, but were unable to and just created an AttachedFileBlock
            // instead. See createAttachedFileBlock method in BulbPageController.
            if (response.hasOwnProperty('embedFailure')) {
              $translate(response.embedFailure.toLowerCase()).then((fileType) => {
                $translate('attached_file_embed_failure_body', { fileType }).then((body) => {
                  BulbModalService.show({
                    titleCode: 'attached_file_embed_failure_title',
                    bodyCode: body,
                    showCancelButton: false,
                  });
                });
              });
            } else {
              // TODO CLEAN handle more gracefully after release
              if (response.hasOwnProperty('embedSizeExceeded')) {
                let maxSize;
                const fileExtension = response.embedSizeExceeded.toLowerCase();
                if (fileExtension === 'xls' || fileExtension === 'xlsx') {
                  maxSize = 5;
                } else {
                  maxSize = 10;
                }
                $translate(fileExtension).then((fileType) => {
                  $translate('attached_file_embed_too_large_body', { fileType, maxSize })
                    .then((body) => {
                      AeroDialogService.showDialog({
                        icon: 'fas fa-exclamation-triangle',
                        iconClass: 'aero-alert',
                        title: $translate.instant('attached_file_embed_failure_title'),
                        detail: body,
                        primaryLabel: $translate.instant('button_ok'),
                        showSecondary: false,
                      });
                    });
                });
                const stopBusyIndicator = BulbCKEStateService.stopBusyIndicator.value;
                if (stopBusyIndicator) {
                  stopBusyIndicator();
                  BulbCKEStateService.stopBusyIndicator.next(null);
                }
              }

              that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

              // Update share status to reflect that the page has been edited:
              const ShareTransition = UnitStateStore.getShareTransitionEnum();
              UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

              const eventName = uploadedToBulb ? 'assetLibraryUploadCompleted' : 'assetLibraryFileResponse';
              const customFileEvent = new CustomEvent(eventName, {
                detail: {
                  contentBlock: response.contentBlock,
                  page: that.page,
                  trackingId: entry.id,
                },
              });
              $window.dispatchEvent(customFileEvent);
            }
          }),
      });
    },

    deleteSelectedBlock: function deleteSelectedBlock() {
      const that = this;
      let $selectedBlock = $(this.selection).closest('content-block');
      const contentBlock = this.page.unit.contentBlocks[$selectedBlock.attr('id')];

      // TODO ISSUE : disable keyboard interaction during dialog, or else the dialog isn't really modal.

      $translate(`content_block_type_${contentBlock.blockType}`).then((type) => {
        $translate('modal_delete_confirm_body', { type }).then((bodyText) => {
          BulbModalService.show({
            titleCode: 'modal_delete_confirm_title',
            bodyText,
            okButtonCode: 'button_delete',
            parent: '.bulb-page-content',
            windowClass: 'bulb-media-transient',
          }).then(() => {
            SerialSaveService.save({
              showBusy: true,
              save: () => Restangular.one('units', that.page.unit.id)
                .one('blocks', contentBlock.id)
                .remove()
                .then(() => {
                  delete that.page.unit.contentBlocks[contentBlock.id];

                  // Update share status to reflect that the page has been edited:
                  const ShareTransition = UnitStateStore.getShareTransitionEnum();
                  UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

                  // Clicking on the modal dialog causes the selection to change (as can keyboard
                  // interaction), so get the block based on the id, so we can remove it.  Then set the
                  // selection caret, so it remains at the same location as the removed block within
                  // the editor.
                  $selectedBlock = $(`#${contentBlock.id}`);
                  $selectedBlock = $('<p><br/></p>').replaceAll($selectedBlock);
                  that.placeSelectionCaretAtStart($selectedBlock[0]);
                  that.unselectBlock();
                  that.trigger('editableInput', null, that.editor);
                  // Explicitly hide the toolbar when a user deletes a content-block
                  const toolbar = that.base.getExtensionByName('toolbar');
                  $timeout(() => {
                    toolbar.hideToolbar();
                  }, CONTENT_EDITOR.GRAB_TOOLBAR_TIMEOUT);
                }),
            });
          });
        });
      });
    },

    /**
     * Create an embed block on a page.
     *
     * @param form
     * @param url
     * @param modalInstance
     * @param attachedFileAssetId
     * @param googleFileId
     * @param googleCode
     * @param uploadedToBulb
     */
    createEmbedBlock: function createEmbedBlock(form, url, modalInstance, attachedFileAssetId, googleFileId, googleCode, uploadedToBulb, isUrlPasteEvent = false) {
      const that = this;

      const embedBlockDTO = {
        url,
        attachedFileAssetId: attachedFileAssetId || null,
      };

      SerialSaveService.save({
        showBusy: true,
        preventRetry: true,
        save: () => Restangular.one('units', that.page.unit.id)
          .customPOST(embedBlockDTO, 'embedblock')
          .then((response) => {
            // Check if embed doc is a Google asset so request api to generate snapshot pdf
            // If the embed doc is a Google form then skip creating pdf snapshot.
            if (googleFileId && url.indexOf('forms/') === -1) {
              BulbAssetLibrarySnapshotPdfService.importPdfFromGoogleDrive(
                googleFileId,
                googleCode,
                response.contentBlock.id,
                that.page.unit.id,
              );
            }

            that.page.unit.contentBlocks[response.contentBlock.id] = response.contentBlock;

            // Update share status to reflect that the page has been edited:
            const ShareTransition = UnitStateStore.getShareTransitionEnum();
            UnitStateStore.transitionShareStatus(ShareTransition.EDIT);

            const customEmbedEvent = new CustomEvent('bulbMediaEmbedResponse', {
              detail: {
                contentBlock: response.contentBlock,
                page: that.page,
              },
            });
            $window.dispatchEvent(customEmbedEvent);

            if (modalInstance) {
              modalInstance.close();
            } else {
              BulbAssetLibraryService.dialog.dismissAllModals();
            }
          }),
      }).catch((error) => {
        if (modalInstance) {
          modalInstance.close();
        }
        if (error.data.errorMap && form) {
          // allow the form controller to handle field validation errors:
          form.handleRemoteErrors(error);
        } else if (googleFileId) {
          // Check if file is google asset to get permission from user to make url public.
          BulbAssetLibraryGoogleDriveService.getShareableLinkForUrl(googleFileId)
            .then((response) => {
              that.createEmbedBlock.call(
                that,
                form,
                response.url,
                modalInstance,
                null,
                googleFileId,
                response.code,
              );
            }, (getShareableLinkError) => {
              if (getShareableLinkError.code === 403) {
                BulbAssetLibrarySnapshotPdfService.importPdfForOrgRestrictedDoc(
                  googleFileId,
                  that.page.unit.id,
                  googleCode,
                ).then((response) => {
                  that.createAttachedFileBlock.call(that, response, false);
                });
              }
            });
        } else {
          BulbModalService.show({
            fontAwesomeCode: 'fas fa-comment-exclamation',
            titleCode: 'modal_oops_title',
            bodyCode: 'pageview_embed_not_supported',
            showCancelButton: false,
            windowClass: 'updated-modal-design bulb-aero-modal',
          }).then(() => {
            const embedURLErrorEvent = new CustomEvent('embedURLErrorEvent', {});
            window.dispatchEvent(embedURLErrorEvent);
          });
        }
      });
    },

    appendNewLine: function appendNewLine(target) {
      return createNewParagraphData().then((data) => target.after(data));
    },

    isLastContent: function isLastContent(target) {
      return target.next().hasClass('bulb-content-trailer');
    },

    focusPageTitle: function focusPageTitle(offset) {
      const $title = $('.bulb-page-title');

      // blur
      $(window.getSelection().getRangeAt(0).startContainer).blur();

      // focus
      $title.focus();

      if (offset === undefined) {
        offset = $title.val().length;
      }

      const title = $title[0];
      if (title.createTextRange) {
        const range = title.createTextRange();
        range.move('character', offset);
        range.select();
      } else if (title.setSelectionRange) {
        title.setSelectionRange(offset, offset);
      }

      return $title;
    },

    handleYouTubeAndVimeoVideoEmbed: function handleYouTubeAndVimeoVideoEmbed(videoUrl,  isUrlPasteEvent = false) {
      const that = this;

      // Handle YouTube channel and videos embedding.
      if (videoUrl.indexOf('youtube.com') > -1 || videoUrl.indexOf('youtu.be') > -1 || videoUrl.indexOf('v=') > -1) {
        that.createEmbedBlock.call(that, null, videoUrl, null, null, null, null, null, isUrlPasteEvent);
      } else {
        // Handle Vimeo video embedding.
        BulbAssetLibraryService.api.createExternalServiceVideo(null, videoUrl)
          .then((res) => {
            BulbAssetLibraryService.dialog.dismissAllModals();
            that.createVideoBlock(res.entry, false);
          }, (error) => {
            BulbErrorService.showError(error.data.message, error).then(() => {
              BulbAssetLibraryService.dialog.closeCurrentModal();
            });
          });
      }

      return $q((resolve) => {
        resolve();
      });
    },

    isYouTubeOrVimeoUrl: function isYouTubeOrVimeoUrl(url) {
      return (url.indexOf('vimeo.com') > -1 || url.indexOf('youtube.com') > -1
        || url.indexOf('youtu.be') > -1 || url.indexOf('v=') > -1);
    },
  });

  return {
    CreateExtension: function CreateExtension(options) {
      return new BulbInsertBlockExtension(options);
    },
  };
}
